<template>
  <div>
    <p class="form-text">
      {{ $t('long_term_application.one_selection_notice') }}
    </p>
    <div class="choice-cards">
      <long-term-exchange-choice-card
        v-model:exchange="applicationData.choice"
        :title="$t('long_term_application.selected_choice')"
        @show-select-exchange="showSelectExchangeModal"
      />
    </div>

    <teleport
      append
      to="#vue-portal-target"
    >
      <remote-modal
        ref="selectExchangeModal"
        :allow-keyboard="true"
        :hide-content-when-loading="true"
        :loading-indicator-in-footer="false"
        :loading-message="$t('application_form.select_exchange.loading')"
        :modal-title="$t('application_form.select_exchange.title')"
        modal-class="modal-tabular"
        modal-dialog-class="modal-xl"
        @details-loaded="onSelectExchangesLoaded"
      >
        <template
          v-if="selectExchangesListLoaded"
          v-slot:modal-content
        >
          <div
            v-if="selectExchangesList.length > 0"
            class="table-wrapper"
          >
            <div class="table-responsive">
              <table class="table table-hover table-sm">
                <thead>
                  <tr>
                    <th>{{ $t('long_term_application.choice.institution_name') }}</th>
                    <th>{{ $t('long_term_application.choice.available_places') }}</th>
                    <th>{{ $t('long_term_application.choice.duration') }}</th>
                    <th class="tight" />
                    <th class="tight" />
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="exchange of selectExchangesList"
                    :key="exchange.id"
                  >
                    <td>{{ exchange.institutionName }}</td>
                    <td>{{ exchange.availablePlaces }}</td>
                    <td>{{ exchange.duration }}</td>
                    <td class="tight">
                      <a
                        v-if="exchangeRequirementsVisible(exchange)"
                        href="#"
                        @click.prevent="showExchangeRequirements(exchange)"
                      >
                        {{ $t('long_term_application.choice.view_requirements') }}
                      </a>
                    </td>
                    <td class="tight">
                      <button
                        class="btn btn-sm btn-info"
                        type="button"
                        @click.prevent="selectExchange(exchange)"
                      >
                        {{ $t('long_term_application.choice.select') }}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div
            v-else
            class="alert alert-warning"
          >
            {{ $t('application_form.select_exchange.no_exchanges_found') }}
          </div>
        </template>
        <template v-slot:modal-footer>
          <button
            class="btn btn-sm btn-secondary"
            data-bs-dismiss="modal"
            type="button"
          >
            {{ $t('modal.button.close') }}
          </button>
        </template>
      </remote-modal>
      <remote-modal
        ref="exchangeRequirementsModal"
        :allow-keyboard="true"
        :modal-title="$t('application_form.exchange_requirements_modal.title')"
        modal-dialog-class="modal-xl exchange-requirements"
        @details-loaded="onExchangeRequirementsLoaded"
      >
        <template v-slot:modal-content>
          <div
            v-if="exchangeRequirementsLoaded"
            class="requirement-list"
          >
            <div
              class="requirement"
              v-html="exchangeRequirements"
            />
          </div>
        </template>
      </remote-modal>
      <modal-alert
        ref="modalAlert"
        :close-button-title="$t('modal.button.close')"
      />
    </teleport>
  </div>
</template>

<script setup lang="ts">
import {computed, onMounted, ref, useTemplateRef, watch} from 'vue'
import {useTranslation} from 'i18next-vue'
import Jsona from 'jsona'

import ModalAlert from '../../vue-components/modal-alert.vue'
import RemoteModal from '../../vue-components/remote-modal.vue'
import LongTermExchangeChoiceCard from './long-term-exchange-choice-card.vue'

import type {LongTermExchangeChoice} from '../types'
import {isNonEmptyString} from '../../js-utils'

const {t: $t} = useTranslation()

interface LongTermApplication {
  choice: LongTermExchangeChoice | null;
}

const props = defineProps({
  availableExchangesUrl: {required: true, type: String},
})

const selectExchangeModal = useTemplateRef<InstanceType<typeof RemoteModal>>('selectExchangeModal')
const exchangeRequirementsModal = useTemplateRef<InstanceType<typeof RemoteModal>>('exchangeRequirementsModal')
const modalAlert = useTemplateRef<InstanceType<typeof ModalAlert>>('modalAlert')

const selectExchangesList = ref<LongTermExchangeChoice[]>([])
const selectExchangesListLoaded = ref(false)
const applicationData = ref<LongTermApplication>({
  choice: null,
})
const applicationDataEl = ref<HTMLInputElement | null>(null)
const exchangeRequirements = ref<string | null>(null)
const functionSelectEl = ref<HTMLSelectElement | null>(null)

const exchangeRequirementsLoaded = computed(() => isNonEmptyString(exchangeRequirements.value))

watch(
  applicationData,
  () => {
    storeApplicationData()
  },
  {deep: true}
)

onMounted(() => {
  applicationDataEl.value = document.querySelector('input[ref="applicationDataJson"]')
  if (applicationDataEl.value) {
    try {
      applicationData.value = JSON.parse(applicationDataEl.value.value)
    } catch {
      // Ignore parse errors
    }
  }

  const el = document.querySelector('select[ref="function"]')
  if (el instanceof HTMLSelectElement) {
    functionSelectEl.value = el
  }
})

const showSelectExchangeModal = (): void => {
  selectExchangesListLoaded.value = false
  const functionId = functionSelectEl.value?.value
  if (isNonEmptyString(functionId)) {
    selectExchangeModal.value?.showModal(props.availableExchangesUrl, {
      function_id: functionId
    })
    return
  }

  modalAlert.value?.show(
    $t('application_form.select_exchange.error.no_function_selected.title'),
    $t('application_form.select_exchange.error.no_function_selected.message'),
    'danger'
  )
}

const onSelectExchangesLoaded = (data: any): void => {
  selectExchangesListLoaded.value = true
  const loadedData = new Jsona().deserialize(data)
  if (Array.isArray(loadedData)) {
    selectExchangesList.value = loadedData as LongTermExchangeChoice[]
  }
}

const selectExchange = (exchange: LongTermExchangeChoice | null): void => {
  selectExchangeModal.value?.hideModal()

  if (exchange) {
    applicationData.value.choice = prepareExchange(exchange, applicationData.value.choice)
  }
}

const prepareExchange = (exchange: LongTermExchangeChoice, previousExchange: LongTermExchangeChoice | null): LongTermExchangeChoice => {
  // copy exchange
  exchange = JSON.parse(JSON.stringify(exchange))

  if (previousExchange) {
    if (previousExchange.durationId) {
      const durationExists = exchange.durationItems.some(x => x.id == previousExchange.durationId)
      if (durationExists) {
        exchange.durationId = previousExchange.durationId
      }
    }
    exchange.specialInterest = previousExchange.specialInterest
  }
  return exchange
}

const storeApplicationData = (): void => {
  if (applicationDataEl.value) {
    applicationDataEl.value.value = JSON.stringify(applicationData.value)
  }
}

const exchangeRequirementsVisible = (exchange: LongTermExchangeChoice): boolean => {
  return isNonEmptyString(exchange.links?.requirements)
}

const showExchangeRequirements = (exchange: LongTermExchangeChoice): void => {
  exchangeRequirements.value = null
  if (isNonEmptyString(exchange.links?.requirements)) {
    exchangeRequirementsModal.value?.showModal(exchange.links.requirements)
  }
}

const onExchangeRequirementsLoaded = (data: unknown): void => {
  if (isNonEmptyString(data)) {
    exchangeRequirements.value = data
  }
}
</script>
